<template>
  <section
    :class="[
      'panel bg-theme-surface dark:bg-theme-surface border border-theme-border dark:border-theme-border shadow-sm dark:shadow-theme-shadow/50',
      { 'panel--interaction-active': interactionActive },
    ]"
  >
    <header v-if="showHeader && title" class="panel-header border-theme-border dark:border-theme-border">
      <div class="panel-title">
        <div class="title text-theme-text dark:text-theme-text/87">{{ title }}</div>
        <div v-if="subtitle" class="subtitle text-theme-text-secondary dark:text-theme-text/60">{{ subtitle }}</div>
      </div>
      <div class="panel-actions">
        <slot name="actions" />
      </div>
    </header>

    <div class="panel-body">
      <slot />
    </div>
  </section>
</template>

<script setup>
import { defineProps } from 'vue';
defineProps({
  title: { type: String, default: null },
  subtitle: { type: String, default: null },
  showHeader: { type: Boolean, default: true },
  interactionActive: { type: Boolean, default: false },
})
</script>

<style scoped>
.panel {
  width: 100%;
  height: 100%;
  border-radius: 14px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-width: 0;
  min-height: 0;
  transition: border-color 120ms ease, box-shadow 120ms ease;
}

.panel.panel--interaction-active {
  border-color: theme(colors.emerald.200) !important;
  box-shadow: 0 0 0 1px rgb(var(--color-brand-primary) / 0.2), 0 1px 2px 0 rgb(var(--color-static-black) / 0.05) !important;
}

.dark .panel.panel--interaction-active {
  border-color: rgb(var(--color-brand-primary) / 0.45) !important;
  box-shadow: 0 0 0 1px rgb(var(--color-brand-primary) / 0.3), 0 1px 2px 0 rgb(var(--color-static-black) / 0.45) !important;
}

.panel-header {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  border-bottom-width: 1px;
  align-items: center;
  min-height: 44px;
  flex-shrink: 0;
}

.panel-title {
  flex: 1;
  min-width: 0;
}

.title {
  font-size: 17px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.subtitle {
  font-size: 12px;
  margin-top: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.panel-actions {
  flex-shrink: 0;
  display: flex;
  gap: 8px;
  align-items: center;
}

.panel-body {
  flex: 1;
  min-height: 0;
  min-width: 0;
  padding: 8px;
  overflow: hidden;
}

/* On mobile stack: allow panel to grow naturally */
@media (max-width: 1024px) {
  .panel {
    height: auto;
    min-height: 360px;
  }

  .panel-body {
    min-height: 300px;
  }
}
</style>

<style>
/* Global overrides for embedded charts inside PanelShell - opt-in via ui.* classes.
   These are intentionally unscoped so they apply to GraphVisualization rendered as a slot child. */
.dash-chart-shell {
  background: transparent !important;
  border: 0 !important;
  box-shadow: none !important;
  border-radius: 0 !important;
  padding: 0 0.3rem !important;
  height: 100%;
  display: flex;
  flex-direction: column;
}

/* Compact header for embedded charts */
.dash-chart-header {
  margin-bottom: 8px !important;
  padding-bottom: 8px !important;
  border-bottom: 1px solid rgb(var(--color-border-subtle)) !important;
}

.dark .dash-chart-header {
  border-bottom-color: rgb(var(--color-surface-inverse)) !important;
}

.dark .dash-chart-header .chart-title {
  color: rgb(var(--color-static-white) / 0.87) !important;
}

.dark .dash-chart-header .chart-subtitle {
  color: rgb(var(--color-static-white) / 0.6) !important;
}

.dash-chart-header .chart-title {
  font-size: 20px !important;
  font-weight: 600 !important;
  margin: 0 !important;
}

.dash-chart-header .chart-subtitle {
  font-size: 11px !important;
  margin-top: -2px !important;
}

.dash-chart-header .control-pill {
  min-width: 100px !important;
}

.dash-chart-header .control-label {
  font-size: 9px !important;
}

.dash-chart-header .control-input {
  height: 32px !important;
  font-size: 12px !important;
  padding: 0 8px !important;
}

.dash-chart-shell .chart-last-updated {
  left: 0rem !important;
  bottom: 0rem !important;
}

.dash-chart-shell .chart-menu {
  right: 0rem !important;
  bottom: 0rem !important;
}

/* Make chart body fill panel */
.dash-chart-body {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding-bottom: 0 !important;
}

.dash-chart-root {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  min-height: 0;
}
</style>
